import { useMenuContentContext } from "../MenuContext";
import type { TypeInternalItemProps } from "../types";
import type { TypeMenuItemProps } from "./MenuItemTypes";
import { MenuItemContainer, StyledMenuItem } from "./styles";
import { useOptionProps } from "./useOptionProps";

// TODO: decide if we want to support inputs on menuitems
// const roles = {
//   checkbox: "menuitemcheckbox",
//   switch: "menuitemcheckbox",
//   radio: "menuitemradio",
// } as const;

const MenuItem = ({
  id,
  children,
  onClick,
  onKeyDown,
  disabled = false,
  active,
  innerRef,
  href,
  color,
  ...props
}: TypeMenuItemProps) => {
  const {
    getItemProps,
    itemsMap,
    highlightedIndex,
    isListbox,
    hiddenItemsMap = {},
  } = useMenuContentContext();
  const { ...optionProps } = useOptionProps({
    id,
    children,
    ...props,
  });
  const item = (itemsMap[id] || { index: 0, id }) as TypeInternalItemProps;

  const highlighted = highlightedIndex === item?.index;

  const downshiftItemProps =
    getItemProps?.({
      ...(isListbox ? {} : { role: "menuitem" }),
      item,
      index: item.index,
      // The disabled prop no longer supported in downshift. Use isItemDisabled instead.
      // disabled,
      onClick,
      onKeyDown,
      ref: innerRef,
    }) ?? {};

  // downshift's useSelect adds aria-selected to every item, but aria-selected
  // is only valid on role="option" (listbox). For menuitem / menuitemcheckbox /
  // menuitemradio it fails axe's aria-allowed-attr rule — menu items use
  // aria-checked for state, not aria-selected.
  if (!isListbox && "aria-selected" in downshiftItemProps) {
    delete downshiftItemProps["aria-selected"];
  }

  return hiddenItemsMap[item.id] ? (
    <></>
  ) : (
    <MenuItemContainer role="none">
      <StyledMenuItem
        id={id}
        $highlighted={highlighted}
        $active={active}
        {...downshiftItemProps}
        // use anchor tag if href is provided and button for menuitem
        as={isListbox ? undefined : href ? "a" : "button"}
        type={href ? undefined : "button"}
        href={href}
        children={children}
        // Keyboard nav uses the arrow keys, not tab.
        tabIndex={-1}
        {...optionProps}
        {...props}
      />
    </MenuItemContainer>
  );
};

MenuItem.__MENU_PRIMITIVE_TYPE = "MenuItem";

export { MenuItem };
